import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { all_routes } from '../router/all_routes'
import Table from "../../core/common/dataTable/index";
import { expenses_details } from '../../core/data/json/expenses_details';
import CollapseHeader from '../../core/common/collapse-header/collapse-header';
import StatusFilterHeader from '../../core/common/commonComponents/StatusFilterHeader';
import { AppDispatch } from '../../core/data/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../core/common/commonComponents/PageHeader';
import { exportToCSV, exportToExcel, exportToPDF } from '../../core/common/GlobalFunctions/exportTableData';
import ExpenseModal from './modals/expenseModal';
import { deleteExpense, fetchAllExpenses, fetchExpensesByEmployeeId } from '../../core/data/redux/payroll/Accounting/expenses/expenseActions';
import { filterData } from '../../core/common/GlobalFunctions/tableFilters';
import DeleteModal1 from '../../core/modals/deleteModal1';
import { message } from 'antd';
import { formatDate } from '../../core/utils/configFormatters';
import { useAppConfig } from '../../customhooks/useAppConfig';
import { decryptData } from '../../core/utils/encryptUtils';
import { getEmployeeByUserId } from '../../core/data/redux/employee/employeeAction';
import FilterHeader from '../mainMenu/requestDashboard/FilterHeader';

const Expenses = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { expenseByEmployee } = useSelector((state: any) => state.accounting.expenses);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const { user } = useSelector((state: any) => state.auth);
    const employeeId = employeesbyUser?.employeeId;

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };
    useEffect(() => {
        dispatch(fetchExpensesByEmployeeId(employeeId))
    }, [dispatch])

    useEffect(() => {
        if (!employeesbyUser?.employeeId) {
            dispatch(getEmployeeByUserId({ userId: user?.UserId }));
        }
    }, [dispatch])

    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");


    const handleDeleteTax = async (id: number) => {
        try {
            const result = await dispatch(deleteExpense(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Reimbursement deleted successfully')
                await dispatch(fetchExpensesByEmployeeId(employeeId))
                setTableKey((prevKey) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.error((result.payload as { error: string })?.error);
            }
        } catch (error) {
            message.error('Something went wrong');
        }
    };
    const filtereddata = useMemo(() => {
        const filters = selectedStatus && selectedStatus !== "All"
            ? { requestStatus: selectedStatus }
            : {};
        return filterData(expenseByEmployee, filters, "createdAt", "createdAt", selectedSort);
    }, [expenseByEmployee, selectedStatus, selectedSort]);

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, expenseByEmployee]);

    const data = expenses_details;

    const config = useAppConfig();
    if (!config) return null;

    const columns = [
        {
            title: "Reimbursement Name",
            dataIndex: "expense_name",
            sorter: (a: any, b: any) => a.expense_name.length - b.expense_name.length,
        },
        {
            title: "Date",
            dataIndex: "expense_date",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => a.expense_date.length - b.expense_date.length,
        },
        {
            title: "Payment Method",
            dataIndex: "payment_method",
            sorter: (a: any, b: any) => a.payment_method.length - b.payment_method.length,
        },
        {
            title: "Amount",
            dataIndex: "amount",
            sorter: (a: any, b: any) => a.amount.length - b.amount.length,
        },
        {
            title: "Request Status",
            dataIndex: "requestStatus",
            sorter: (a: any, b: any) => a.requestStatus.length - b.requestStatus.length,
            render: (status: string) => {
                let color = "";
                switch (status) {
                    case "Approved":
                        color = "green";
                        break;
                    case "Rejected":
                        color = "red";
                        break;
                    case "Pending":
                        color = "orange";
                        break;
                    default:
                        color = "gray";
                }
                return <span style={{ color, fontWeight: 600 }}>{status}</span>;
            },
        },
        {
            title: "Payment Status ",
            dataIndex: "paymentStatus",
            render: (text: string, record: any) => (
                <span className={`badge ${text === 'Paid' ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}>
                    <i className="ti ti-point-filled me-1" />
                    {text}
                </span>

            ),
        },
        {
            title: "File",
            dataIndex: "doc_path",
            render: (filePath: string) => {
                if (!filePath) return "No File";

                const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
                const extension = filePath.split('.').pop()?.toLowerCase();

                const isImage = imageExtensions.includes(extension || "");

                return (
                    <a
                        href={filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        title={isImage ? "View Image" : "Download File"}
                    >
                        <i className={`ti ${isImage ? 'ti-eye' : 'ti-download'}`} />
                    </a>
                );
            },
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (text: string, record: any) => {
                const isApproved = record.requestStatus === "Approved";
                return (
                    <div>
                        <div className="action-icon d-inline-flex">
                            {/* Edit Button */}
                            {canEdit && (
                                <button
                                    className={`me-2 ${isApproved ? "opacity-50 cursor-not-allowed" : ""}`}
                                    disabled={isApproved}
                                    onClick={() => {
                                        if (isApproved) return;
                                        showModal('edit');
                                        setSelectedRowData(record);
                                    }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title="Edit"
                                >
                                    <i className="ti ti-edit" />
                                </button>
                            )}

                            {/* Delete Button */}
                            {canDelete && (
                                <button
                                    className={`me-2 ${record.status === "inactive" || isApproved ? "opacity-50 cursor-not-allowed" : ""}`}
                                    disabled={record.status === "inactive" || isApproved}
                                    onClick={() => {
                                        if (record.status === "inactive" || isApproved) return;
                                        setSelectedRowData(record);
                                        setIsDeleteModalVisible(true);
                                    }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title="Delete"
                                >
                                    <i className="ti ti-trash" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            },
        },
    ]

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <PageHeader
                        title="Reimbursement"
                        breadcrumbs={[
                            { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
                            { label: "Accounting" },
                            { label: "Reimbursement List", isActive: true },
                        ]}
                        // listViewPath={all_routes.clientlist}
                        listViewPath="#"
                        onExportCSV={canExport ? () => exportToCSV(data, 'ReimbursementData') : undefined}
                        onExportExcel={canExport ? () => exportToExcel(data, 'ReimbursementData') : undefined}
                        onExportPDF={canExport ? () => exportToPDF(data, 'ReimbursementData') : undefined}
                        onAdd={canAdd ? () => showModal('create') : undefined}
                        addLabel="Add Reimbursement"
                        exportEnabled={canExport}
                        addButtonEnabled={canAdd}
                        rightExtra={<CollapseHeader />}
                    />
                    {/* /Breadcrumb */}
                    <div className="card">
                        <FilterHeader
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                        />
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filtereddata} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            <ExpenseModal modalType={modalType} handleCancel={handleCancel} isModalVisible={isModalVisible} selectedRowData={selectedRowData} setTableKey={setTableKey} employeeId={employeeId} />
            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.expense_id && handleDeleteTax(selectedRowData.expense_id)}
            />
        </>
    )
}

export default Expenses
